import { useId, useState } from 'react';
import { formatMoney } from '@/lib/booking';
import { m } from '@/paraglide/messages';
import type { PriceLineItem, NightRate } from '@/api/generated';

export interface PriceBreakdownProps {
  lineItems: PriceLineItem[] | null | undefined;
  nights: NightRate[] | null | undefined;
  totalMinor: number;
  currency: string;
}

// Paraglide labels, keyed by kind. A kind absent from this map (an older
// client talking to a newer API — Cloudflare Pages deploys before EC2 on
// every release) falls back to the server-supplied `label` in `labelFor`
// below, never a blank row.
const KIND_LABELS: Record<PriceLineItem['kind'], () => string> = {
  nightly_rate: m.price_breakdown_kind_nightly_rate,
  extra_guest_fee: m.price_breakdown_kind_extra_guest_fee,
  pet_fee: m.price_breakdown_kind_pet_fee,
  tourist_tax: m.price_breakdown_kind_tourist_tax,
  los_discount: m.price_breakdown_kind_los_discount,
  early_bird_discount: m.price_breakdown_kind_early_bird_discount,
  direct_member_discount: m.price_breakdown_kind_direct_member_discount,
  promo_discount: m.price_breakdown_kind_promo_discount,
};

function labelFor(item: PriceLineItem): string {
  const translate = KIND_LABELS[item.kind];
  return translate ? translate() : item.label;
}

// The total row's own markup — identical whether or not there are line
// items, so a surface with no breakdown renders exactly what it renders
// today (§11: "no skeleton, no unavailable copy").
function TotalRow({ totalMinor, currency }: { totalMinor: number; currency: string }) {
  return (
    <div className="flex items-baseline justify-between rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-3 py-2.5">
      <span className="text-sm text-muted">Total</span>
      <span className="font-mono text-lg font-semibold tabular-nums text-[var(--ollio-ink)]">
        {formatMoney(totalMinor, currency)}
      </span>
    </div>
  );
}

// PriceBreakdown renders the two-level guest-facing itemization (design doc
// §11): line items + total visible by default, per-night detail behind a
// collapsed disclosure. Props are flat, not a wire shape — the quote sends
// line_items/night_rates at the top level, the hold and booking view send a
// nested price_breakdown document; each surface unpacks what it has and this
// component never learns which endpoint it came from.
export function PriceBreakdown({ lineItems, nights, totalMinor, currency }: PriceBreakdownProps) {
  const [open, setOpen] = useState(false);
  const regionId = useId();

  const items = lineItems ?? [];
  const nightRows = nights ?? [];

  if (items.length === 0) {
    return <TotalRow totalMinor={totalMinor} currency={currency} />;
  }

  // unit_minor is a display average (never re-derive an amount from it) — the
  // one place it appears is this disclosure trigger, explicitly labelled
  // "avg". The trigger is only meaningful next to the accommodation line it
  // summarizes, so with no nightly_rate line (shouldn't happen today — the
  // pricer always produces one alongside nights — but the type doesn't
  // guarantee it) the disclosure does not render at all rather than show a
  // vague, unlabelled night count.
  const nightlyItem = items.find((i) => i.kind === 'nightly_rate');
  const showDisclosure = nightRows.length > 0 && nightlyItem != null;
  const avg = nightlyItem ? formatMoney(nightlyItem.unit_minor, currency) : '';
  const toggleLabel =
    nightlyItem?.qty === 1
      ? m.price_breakdown_toggle_one_night({ avg })
      : m.price_breakdown_toggle_nights({ nights: nightlyItem?.qty ?? 0, avg });

  return (
    <div className="space-y-2">
      <ul className="space-y-1">
        {items.map((item, idx) => {
          const isDiscount = item.amount_minor < 0;
          return (
            <li key={idx} className="flex items-baseline justify-between text-sm">
              <span className="text-muted">{labelFor(item)}</span>
              <span
                className={
                  isDiscount
                    ? 'tabular-nums text-[var(--ollio-success)]'
                    : 'tabular-nums text-[var(--ollio-ink)]'
                }
              >
                {formatMoney(item.amount_minor, currency)}
              </span>
            </li>
          );
        })}
      </ul>

      {showDisclosure ? (
        <div>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={regionId}
            onClick={() => setOpen((o) => !o)}
            className="text-xs font-medium text-[var(--ollio-gold-deep)]"
          >
            {toggleLabel} <span aria-hidden="true">›</span>
          </button>
          <ul
            id={regionId}
            hidden={!open}
            className="mt-1 space-y-0.5 border-t border-[var(--border-hairline)] pt-1"
          >
            {nightRows.map((night) => (
              <li key={night.date} className="flex items-baseline justify-between text-xs text-muted">
                <span>{night.date}</span>
                <span className="tabular-nums">{formatMoney(night.rate_minor, currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <TotalRow totalMinor={totalMinor} currency={currency} />
    </div>
  );
}
